import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Save, Loader2, UserPlus, Lock, Info, ShieldBan, ShieldCheck, History, Bot, Globe, Ban, X } from 'lucide-react';

const parseDomains = (text) => Array.from(new Set(
  (text || '')
    .split(/[\s,;]+/)
    .map((d) => d.toLowerCase().trim().replace(/^@/, '').replace(/^https?:\/\//, ''))
    .filter(Boolean)
));

/**
 * SuperAdmin > Registration tab body.
 * Rendered inline inside SuperAdmin.js via {activeTab === 'registration'}.
 * No page wrapper / top-nav — the SuperAdmin shell provides those.
 */
export default function RegistrationConfigTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [closedMessage, setClosedMessage] = useState('');
  const [blockedDomains, setBlockedDomains] = useState('');
  const [allowedDomains, setAllowedDomains] = useState('');
  const [mxCheck, setMxCheck] = useState(true);
  const [audit, setAudit] = useState([]);
  const [signups, setSignups] = useState({ summary: {}, attempts: [], banned: [] });
  const [banIpInput, setBanIpInput] = useState('');

  useEffect(() => {
    reload();
    loadAudit();
    loadSignups();
  }, []);

  const loadSignups = async () => {
    try {
      const { data } = await api.get('/super-admin/config/registration/signups?limit=200');
      setSignups({ summary: data?.summary || {}, attempts: data?.attempts || [], banned: data?.banned_ips || [] });
    } catch (e) { /* best-effort */ }
  };

  const banIp = async (ip, reason = 'manual') => {
    const target = (ip || '').trim();
    if (!target) return;
    try {
      await api.post('/super-admin/config/registration/banned-ips', { ip: target, reason });
      toast.success(`Banned ${target}`);
      setBanIpInput('');
      loadSignups();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not ban IP'); }
  };

  const unbanIp = async (ip) => {
    try {
      await api.delete(`/super-admin/config/registration/banned-ips/${encodeURIComponent(ip)}`);
      toast.success(`Unbanned ${ip}`);
      loadSignups();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not unban IP'); }
  };

  const blockIp = async (ip) => {
    // Copy a suspected-bot IP so an admin can act on it elsewhere (firewall / support).
    try { await navigator.clipboard.writeText(ip); toast.success(`Copied ${ip}`); }
    catch (e) { toast.message(ip); }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get('/super-admin/config/registration/audit');
      setAudit(data?.audit || []);
    } catch (e) { /* audit is best-effort */ }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/config/registration');
      setEnabled(!!data?.enabled);
      setClosedMessage(data?.closed_message || '');
      setBlockedDomains((data?.blocked_domains || []).join('\n'));
      setAllowedDomains((data?.allowed_domains || []).join('\n'));
      setMxCheck(data?.mx_check_enabled !== false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load config');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { enabled };
      if (closedMessage && closedMessage.trim()) {
        payload.closed_message = closedMessage.trim();
      }
      payload.blocked_domains = parseDomains(blockedDomains);
      payload.allowed_domains = parseDomains(allowedDomains);
      payload.mx_check_enabled = mxCheck;
      const { data } = await api.put('/super-admin/config/registration', payload);
      setBlockedDomains((data?.blocked_domains || []).join('\n'));
      setAllowedDomains((data?.allowed_domains || []).join('\n'));
      setMxCheck(data?.mx_check_enabled !== false);
      toast.success(data?.enabled ? 'Registration ENABLED' : 'Registration DISABLED');
      loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="registration-config-tab">
      <div className="mb-6">
        <h2 className="text-xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Registration configuration
        </h2>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Control whether visitors can create their own accounts at{' '}
          <code className="bg-slate-100 px-1 py-0.5 text-[11px]">/signup</code>.
          When disabled, the signup form shows a customizable closed-invite message and
          the <code className="bg-slate-100 px-1 py-0.5 text-[11px]">/api/auth/signup</code> endpoint returns 403.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2"/>Loading…
        </div>
      ) : (
        <div className="bg-white border border-slate-200">
          {/* Master switch */}
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-start gap-4">
              <div className={`h-10 w-10 flex items-center justify-center ${enabled ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                {enabled ? <UserPlus className="h-5 w-5"/> : <Lock className="h-5 w-5"/>}
              </div>
              <div>
                <div className="text-base font-black tracking-tight flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Self-serve registration
                  {enabled ? (
                    <Badge className="rounded-none bg-primary/10 text-primary hover:bg-primary/10">ENABLED</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-none text-slate-500">CLOSED</Badge>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {enabled
                    ? 'Anyone with a valid email can create an account and land on Free.'
                    : 'Only invited users can create accounts. /signup shows a closed-invite screen.'}
                </div>
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="reg-config-enabled-switch"
            />
          </div>

          {/* Closed message editor */}
          <div className="px-6 py-5 border-b border-slate-200">
            <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2 block">
              Message shown when registration is closed
            </label>
            <Textarea
              className="rounded-none min-h-[100px] font-normal"
              placeholder="e.g. New sign-ups are currently invite-only. Contact sales@billportal.io to request access."
              value={closedMessage}
              onChange={(e) => setClosedMessage(e.target.value)}
              data-testid="reg-config-closed-message"
            />
            <p className="text-[11px] text-slate-500 mt-2">
              Shown on the <code className="bg-slate-100 px-1 py-0.5 text-[11px]">/signup</code> page and returned
              in the 403 response from <code className="bg-slate-100 px-1 py-0.5 text-[11px]">POST /api/auth/signup</code>.
            </p>
          </div>

          {/* Email domain policy */}
          <div className="px-6 py-5 border-b border-slate-200" data-testid="reg-config-domain-policy">
            <div className="flex items-start gap-4 mb-4">
              <div className="h-10 w-10 flex items-center justify-center bg-slate-100 text-slate-600">
                <ShieldBan className="h-5 w-5" />
              </div>
              <div>
                <div className="text-base font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Business email policy
                </div>
                <div className="text-xs text-slate-500 mt-0.5 max-w-2xl">
                  Trial parser and self-serve registration require a work email. Free/personal
                  providers (gmail, outlook, yahoo, icloud, proton…) and common disposable
                  domains are blocked automatically. Add your own overrides below — one domain
                  per line (or comma-separated). Changes apply immediately, no code change needed.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
                  <ShieldBan className="h-3.5 w-3.5 text-red-600" /> Additional blocked domains
                </label>
                <Textarea
                  className="rounded-none min-h-[120px] font-mono text-xs"
                  placeholder={'competitor.com\nspammydomain.io'}
                  value={blockedDomains}
                  onChange={(e) => setBlockedDomains(e.target.value)}
                  data-testid="reg-config-blocked-domains"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Emails at these domains are rejected on top of the built-in list.
                </p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Force-allowed domains
                </label>
                <Textarea
                  className="rounded-none min-h-[120px] font-mono text-xs"
                  placeholder={'partner-gmail.com\ncontractor@client.co'}
                  value={allowedDomains}
                  onChange={(e) => setAllowedDomains(e.target.value)}
                  data-testid="reg-config-allowed-domains"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Always allowed, even if they'd normally be treated as generic. Allow-list wins over block-list.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className={`h-8 w-8 flex items-center justify-center ${mxCheck ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">DNS / MX verification</div>
                  <div className="text-[11px] text-slate-500 max-w-xl">
                    Reject business emails whose domain can't actually receive mail (no MX/DNS records) —
                    catches typo'd and fake company domains. Fails open if our DNS lookup errors, so real users are never wrongly blocked.
                  </div>
                </div>
              </div>
              <Switch checked={mxCheck} onCheckedChange={setMxCheck} data-testid="reg-config-mx-check" />
            </div>
          </div>

          <div className="px-6 py-4 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <Info className="h-3.5 w-3.5"/>
              Changes apply immediately, no restart required.
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-none"
              data-testid="reg-config-save-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Saving…</>
              ) : (
                <><Save className="h-4 w-4 mr-2"/>Save changes</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Sign-up traffic / IP tracking + bot detection */}
      {!loading && (
        <div className="bg-white border border-slate-200 mt-6" data-testid="reg-config-signups-panel">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 flex items-center justify-center bg-slate-100 text-slate-600">
                <Globe className="h-4 w-4" />
              </div>
              <div>
                <div className="text-base font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Sign-up traffic &amp; IP tracking
                </div>
                <div className="text-xs text-slate-500">Recent registration / trial attempts with IP, device and bot signals.</div>
              </div>
            </div>
            <Button variant="outline" className="rounded-none" onClick={loadSignups} data-testid="reg-signups-refresh">
              <Loader2 className={`h-3.5 w-3.5 mr-2 ${false ? 'animate-spin' : 'hidden'}`} />Refresh
            </Button>
          </div>

          {/* Summary chips */}
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100 border-b border-slate-200">
            {[
              ['Total attempts', signups.summary?.total ?? 0, 'text-slate-800'],
              ['Last 24h', signups.summary?.last24h ?? 0, 'text-slate-800'],
              ['Blocked', signups.summary?.blocked ?? 0, 'text-red-600'],
              ['Bot-suspected', signups.summary?.bots ?? 0, 'text-amber-600'],
              ['Banned IPs', signups.summary?.banned_ips ?? 0, 'text-red-700'],
            ].map(([label, val, cls]) => (
              <div key={label} className="px-4 py-3">
                <div className={`text-2xl font-black tracking-tight ${cls}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{val}</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
              </div>
            ))}
          </div>

          {/* Top offending IPs (24h) — click to ban */}
          {(signups.summary?.top_ips || []).length > 0 && (
            <div className="px-6 py-3 border-b border-slate-200 flex flex-wrap gap-2 items-center">
              <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1">Top IPs (24h):</span>
              {(signups.summary.top_ips || []).map((t) => (
                <span key={t.ip} className={`inline-flex items-center rounded-none border text-xs font-mono ${t.blocked > 0 ? 'border-red-300 text-red-700 bg-red-50' : 'border-slate-200 text-slate-600'}`}>
                  <button onClick={() => blockIp(t.ip)} title="Copy IP" className="px-2 py-0.5" data-testid={`reg-topip-${t.ip}`}>
                    {t.ip} · {t.count}{t.blocked > 0 ? ` (${t.blocked} blk)` : ''}
                  </button>
                  <button onClick={() => banIp(t.ip, 'manual_top_ip')} title="Ban this IP" className="px-1.5 py-0.5 border-l border-inherit hover:bg-red-100 text-red-600" data-testid={`reg-ban-topip-${t.ip}`}>
                    <Ban className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Banned IPs manager */}
          <div className="px-6 py-4 border-b border-slate-200" data-testid="reg-banned-ips-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                <Ban className="h-3.5 w-3.5 text-red-600" /> Banned IPs (auto + manual)
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={banIpInput}
                  onChange={(e) => setBanIpInput(e.target.value)}
                  placeholder="Ban an IP…"
                  data-testid="reg-ban-ip-input"
                  className="rounded-none border border-slate-200 px-2 py-1 text-xs font-mono w-40 focus:outline-none focus:border-primary"
                />
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => banIp(banIpInput)} data-testid="reg-ban-ip-submit">
                  <Ban className="h-3 w-3 mr-1" /> Ban
                </Button>
              </div>
            </div>
            {(signups.banned || []).length === 0 ? (
              <div className="text-xs text-slate-400" data-testid="reg-banned-empty">No IPs are currently banned. IPs are auto-banned after repeated bot-flagged attempts.</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="reg-banned-list">
                {(signups.banned || []).map((b) => (
                  <span key={b.ip} className={`inline-flex items-center rounded-none border text-xs font-mono ${b.active ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-200 text-slate-400 line-through'}`} data-testid={`reg-banned-${b.ip}`}>
                    <span className="px-2 py-0.5" title={`${b.reason || ''}${b.auto ? ' (auto)' : ''}${b.expires_at ? ' · until ' + new Date(b.expires_at).toLocaleString() : ' · permanent'}`}>
                      {b.ip}{b.auto ? ' · auto' : ''}
                    </span>
                    <button onClick={() => unbanIp(b.ip)} title="Unban" className="px-1.5 py-0.5 border-l border-inherit hover:bg-slate-100" data-testid={`reg-unban-${b.ip}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {signups.attempts.length === 0 ? (
            <div className="px-6 py-8 text-sm text-slate-400 text-center" data-testid="reg-signups-empty">No sign-up attempts recorded yet.</div>
          ) : (
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs" data-testid="reg-signups-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-500 uppercase tracking-wide">
                    <th className="p-3">When</th>
                    <th className="p-3">Email / domain</th>
                    <th className="p-3">IP</th>
                    <th className="p-3">Source</th>
                    <th className="p-3">Result</th>
                    <th className="p-3">Signals</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {signups.attempts.map((a) => (
                    <tr key={a.id} data-testid={`reg-signup-row-${a.id}`} className={a.is_bot_suspected ? 'bg-amber-50/40' : ''}>
                      <td className="p-3 whitespace-nowrap text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="p-3">
                        <div className="text-slate-800">{a.email}</div>
                        <div className="text-slate-400">{a.domain}</div>
                      </td>
                      <td className="p-3 font-mono text-slate-600">{a.ip}</td>
                      <td className="p-3"><Badge variant="outline" className="rounded-none text-[10px]">{a.source}</Badge></td>
                      <td className="p-3">
                        {a.result === 'allowed' ? (
                          <Badge className="rounded-none bg-emerald-100 text-emerald-700 hover:bg-emerald-100">allowed</Badge>
                        ) : (
                          <Badge className="rounded-none bg-red-100 text-red-700 hover:bg-red-100">{a.result}{a.reason ? ` · ${a.reason}` : ''}</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        {a.is_bot_suspected && (
                          <span className="inline-flex items-center gap-1 text-amber-700" title={a.bot_reason}>
                            <Bot className="h-3.5 w-3.5" /> bot?
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Policy change audit log */}
      {!loading && (
        <div className="bg-white border border-slate-200 mt-6" data-testid="reg-config-audit-log">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="h-9 w-9 flex items-center justify-center bg-slate-100 text-slate-600">
              <History className="h-4 w-4" />
            </div>
            <div>
              <div className="text-base font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Policy change history
              </div>
              <div className="text-xs text-slate-500">Who changed the block / allow / MX settings, and when.</div>
            </div>
          </div>
          {audit.length === 0 ? (
            <div className="px-6 py-8 text-sm text-slate-400 text-center">No policy changes recorded yet.</div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto">
              {audit.map((row) => (
                <div key={row.id} className="px-6 py-4" data-testid={`reg-audit-row-${row.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-sm font-semibold text-slate-800">
                      {row.user_name || row.user_email || 'Unknown user'}
                      {row.user_email && row.user_name && (
                        <span className="text-xs text-slate-400 font-normal ml-1.5">{row.user_email}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">{new Date(row.at).toLocaleString()}</div>
                  </div>
                  <ul className="space-y-1">
                    {(row.changes || []).map((c, i) => (
                      <li key={i} className="text-xs text-slate-600 flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" className="rounded-none text-[10px]">{c.label}</Badge>
                        {Array.isArray(c.added) || Array.isArray(c.removed) ? (
                          <>
                            {c.added?.length > 0 && (
                              <span className="text-emerald-700">+{c.added.length} added
                                <span className="text-slate-400"> ({c.added.slice(0, 4).join(', ')}{c.added.length > 4 ? '…' : ''})</span>
                              </span>
                            )}
                            {c.removed?.length > 0 && (
                              <span className="text-red-700">−{c.removed.length} removed
                                <span className="text-slate-400"> ({c.removed.slice(0, 4).join(', ')}{c.removed.length > 4 ? '…' : ''})</span>
                              </span>
                            )}
                          </>
                        ) : (
                          <span>
                            <span className="text-slate-400 line-through mr-1">{String(c.before)}</span>
                            <span className="text-slate-800 font-medium">{String(c.after)}</span>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
